import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useLanguageStore } from '../store/languageStore';
import translations from '../translations';
import {
    getShifts as getOnlineShifts,
    endTask,
    clockIn,
    finishJob,
    clockOut,
    getCurrentShift,
    startTask,
} from '../utils/api';
import {
    getShifts as getOfflineShifts,
    saveShifts,
    clearOldData,
    addPendingAction,
} from '../utils/offlineDb';
import {
    setTaskSession,
    updateTaskSession,
    removeTaskSession,
    getAllActiveTasks,
    getTaskSession,
    getShiftSession,
} from '../utils/firebase';

import { isFromPreviousCSTDay, calculateDuration } from '../utils/time';
import {
    sendTelegramMessage,
    formatClockInMessage,
    formatJobFinishMessage,
    formatClockOutMessage,
    formatTaskEndMessage,
} from '../utils/telegram';
import { ActiveTask, Shift } from '../types/time';
import { ActiveTasksModal } from './time/ActiveTasksModal';
import { ConfirmationDialog } from './time/ConfirmationDialog';
import { StatusDisplay } from './time/StatusDisplay';
import { ActionButton } from './time/ActionButton';
import { LoadingSkeleton } from './common/LoadingSkeleton';
import { ErrorState } from './common/ErrorState';
import { DATA_REFRESH_EVENT } from '../utils/syncManager';

export function TimeTracker() {
    const { token, workerId, workerInfo } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<
        'not_clocked_in' | 'working' | 'job_finished' | 'clocked_out'
    >('not_clocked_in');
    const [currentShift, setCurrentShift] = useState<Shift | null>(null);
    const [activeTasks, setActiveTasks] = useState<ActiveTask[]>([]);
    const [showConfirmation, setShowConfirmation] = useState<
        'clockin' | 'finishjob' | 'clockout' | null
    >(null);
    const [showActiveTasks, setShowActiveTasks] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [isInitialOfflineCheck, setIsInitialOfflineCheck] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const { language } = useLanguageStore();
    const t = translations[language];

    // Refresh data when going online
    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);
        };
        const handleOffline = () => setIsOffline(true);
        const handleDataRefresh = () => {
            if (workerId) {
                checkTodayShift();
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
        };
    }, [workerId]);

    useEffect(() => {
        if (workerId) {
            checkTodayShift();
            loadActiveTasks();
        }
    }, [workerId, refreshTrigger]);

    async function handleTaskComplete(taskId: number, startTime: number) {
        if (!token || !workerId || !workerInfo) return;

        if (isOffline) {
            toast.error('You must be online to complete tasks');
            return;
        }

        const taskSession = await getTaskSession(workerId, taskId);
        if (!taskSession) {
            toast.error('Task session not found');
            return;
        }

        setLoading(true);
        try {
            const endTime = Date.now();
            const duration = Math.floor((endTime - startTime) / 1000);

            if (duration < 10) {
                toast.error('Task must run for at least 10 seconds');
                return;
            }

            // End task in backend
            await updateTaskSession(workerId, taskId, { status: 'completed' });
            await endTask(
                token,
                parseInt(workerId, 10),
                taskId,
                startTime,
                endTime,
            );
            await removeTaskSession(workerId, taskId);

            // Send Telegram notification
            if (workerInfo?.groupId) {
                const message = formatTaskEndMessage(
                    workerInfo,
                    {
                        id: taskId,
                        desc: taskSession.description,
                        isVisible: true,
                        isCompleted: false,
                    },
                    {
                        report_id: taskSession.report_id,
                        wo_number: taskSession.wo_number,
                    },
                    calculateDuration(startTime, endTime),
                );
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            await loadActiveTasks();
            setRefreshTrigger((prev) => prev + 1);
            toast.success('Task completed successfully');
        } catch (error) {
            console.error('Failed to complete task:', error);
            toast.error('Failed to complete task');
            await updateTaskSession(workerId, taskId, {
                status: 'in_progress',
            });
        } finally {
            setLoading(false);
        }
    }

    async function loadActiveTasks() {
        if (!workerId || !token) return;
        try {
            const tasks = await getAllActiveTasks(workerId);
            setActiveTasks(tasks);
        } catch (error) {
            toast.error('Error loading active tasks');
        }
    }

    async function checkTodayShift() {
        if (!token || !workerId) return;

        setLoading(true);
        try {
            if (navigator.onLine) {
                try {
                    const currentShift = await getCurrentShift(
                        token,
                        parseInt(workerId, 10),
                    );
                    if (currentShift) {
                        setCurrentShift(currentShift);
                        setStatus(
                            currentShift.clockout_time
                                ? 'clocked_out'
                                : currentShift.finishjob_time
                                  ? 'job_finished'
                                  : 'working',
                        );
                        await loadActiveTasks();
                    } else {
                        setStatus('not_clocked_in');
                        setCurrentShift(null);
                    }

                    const onlineShifts = await getOnlineShifts(token);
                    await saveShifts(onlineShifts);
                    console.log('Saved online shifts to offline storage');
                } catch (error) {
                    throw error;
                }
            } else {
                console.log(
                    'Offline mode: Getting shifts from offline storage',
                );
                const offlineShifts = await getOfflineShifts();
                const todayShift =
                    offlineShifts.find(
                        (shift) =>
                            shift.worker_id === parseInt(workerId, 10) &&
                            !isFromPreviousCSTDay(shift.clockin_time * 1000),
                    ) || null;

                if (todayShift) {
                    const normalizedShift = {
                        ...todayShift,
                        clockin_time: todayShift.clockin_time * 1000,
                        finishjob_time: todayShift.finishjob_time
                            ? todayShift.finishjob_time * 1000
                            : undefined,
                        clockout_time: todayShift.clockout_time
                            ? todayShift.clockout_time * 1000
                            : undefined,
                    };

                    setCurrentShift(normalizedShift);
                    setStatus(
                        normalizedShift.clockout_time
                            ? 'clocked_out'
                            : normalizedShift.finishjob_time
                              ? 'job_finished'
                              : 'working',
                    );
                } else {
                    setStatus('not_clocked_in');
                    setCurrentShift(null);
                }
            }

            setError(null);
        } catch (error) {
            console.error('Failed to check shifts:', error);
            setError('Failed to check shifts');
        } finally {
            setLoading(false);
            setInitialLoading(false);
            setIsInitialOfflineCheck(false);
        }
    }

    const handleClockIn = async () => {
        if (!workerId || !workerInfo) return;

        const now = Date.now();
        setLoading(true);
        try {
            await clockIn(parseInt(workerId, 10));
            await checkTodayShift(); // Refresh shift data

            if (navigator.onLine && workerInfo?.groupId) {
                const message = formatClockInMessage(workerInfo);
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            toast.success('Successfully clocked in');
        } catch (error: any) {
            toast.error(error.message || 'Failed to clock in');
        } finally {
            setLoading(false);
            setShowConfirmation(null);
        }
    };

    const handleFinishJob = async () => {
        if (!currentShift || !workerId || !workerInfo) return;

        if (!navigator.onLine || !token) {
            toast.error('You must be online to finish job');
            return;
        }

        if (activeTasks.length > 0) {
            toast.error(
                'Please complete all active tasks before finishing job',
            );
            return;
        }

        const now = Date.now();
        setLoading(true);
        try {
            await finishJob(currentShift.id);
            await checkTodayShift(); // Refresh shift data

            if (workerInfo?.groupId) {
                const duration = calculateDuration(
                    currentShift.clockin_time,
                    now,
                );
                const message = formatJobFinishMessage(workerInfo, duration);
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            toast.success('Job marked as finished');
        } catch (error) {
            toast.error('Failed to finish job');
        } finally {
            setLoading(false);
            setShowConfirmation(null);
        }
    };

    const handleClockOut = async () => {
        if (!currentShift || !token || !workerId || !workerInfo) return;

        if (!navigator.onLine || !token) {
            toast.error('You must be online to clock out');
            return;
        }

        if (activeTasks.length > 0) {
            toast.error('Please complete all active tasks before clocking out');
            return;
        }

        const now = Date.now();
        setLoading(true);
        try {
            await clockOut(currentShift.id);
            await checkTodayShift(); // Refresh shift data

            if (workerInfo?.groupId) {
                const duration = calculateDuration(
                    currentShift.clockin_time,
                    now,
                );
                const message = formatClockOutMessage(workerInfo, duration);
                await sendTelegramMessage(workerInfo.groupId, message);
            }

            toast.success('Successfully clocked out');
        } catch (error: any) {
            toast.error(error.message || 'Failed to clock out');
        } finally {
            setLoading(false);
            setShowConfirmation(null);
        }
    };

    const handleAction = () => {
        switch (status) {
            case 'not_clocked_in':
                setShowConfirmation('clockin');
                break;
            case 'working':
                setShowConfirmation('finishjob');
                break;
            case 'job_finished':
                setShowConfirmation('clockout');
                break;
        }
    };

    if (initialLoading) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <ErrorState message={error} onRetry={checkTodayShift} />
            </div>
        );
    }

    return (
        <div className='min-h-screen bg-light dark:bg-dark'>
            <div className='max-w-4xl mx-auto p-4'>
                {isOffline && (
                    <div className='mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg flex items-center gap-2'>
                        <span className='text-sm'>
                            You are currently offline. Some features may be
                            limited.
                        </span>
                    </div>
                )}
                <div className='bg-light-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-soft-dark p-6 space-y-6 border border-light-border dark:border-dark-border'>
                    <div className='bg-gray-50 dark:bg-dark-card rounded-lg p-4 border border-light-border dark:border-dark-border'>
                        <h2 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2'>
                            {t.timeTracker.currentStatus}
                        </h2>
                        <p className='text-lg font-medium text-blue-600 dark:text-blue-400 capitalize mb-4'>
                            {status === 'not_clocked_in' &&
                                t.timeTracker.status.notClockedIn}
                            {status === 'working' &&
                                t.timeTracker.status.working}
                            {status === 'job_finished' &&
                                t.timeTracker.status.jobFinished}
                            {status === 'clocked_out' &&
                                t.timeTracker.status.completed}
                        </p>
                        <StatusDisplay
                            currentShift={currentShift}
                            activeTasks={activeTasks}
                            onViewTasks={() => setShowActiveTasks(true)}
                        />
                    </div>

                    <div className='pt-4'>
                        <ActionButton
                            status={status}
                            loading={loading}
                            hasActiveTasks={activeTasks.length > 0}
                            onAction={handleAction}
                        />
                    </div>
                </div>
            </div>

            {showConfirmation && (
                <ConfirmationDialog
                    type={showConfirmation}
                    onConfirm={
                        showConfirmation === 'clockin'
                            ? handleClockIn
                            : showConfirmation === 'finishjob'
                              ? handleFinishJob
                              : handleClockOut
                    }
                    onCancel={() => setShowConfirmation(null)}
                    loading={loading}
                />
            )}

            {showActiveTasks && (
                <ActiveTasksModal
                    tasks={activeTasks}
                    onClose={() => setShowActiveTasks(false)}
                    onTaskComplete={handleTaskComplete}
                    loading={loading}
                />
            )}
        </div>
    );
}
